import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, BookOpen } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FileUploader } from "@/components/FileUploader";
import { toast } from "sonner";

const emptyForm = { title: "", description: "", price: "", coverImage: "", pdfFileUrl: "" };
const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

export const AdminEbookManager = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/ebooks");
      setBooks(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim() || !form.price || !form.pdfFileUrl.trim()) {
      toast.error("Title, description, price and PDF URL are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/ebooks", {
        title: form.title.trim(),
        description: form.description.trim(),
        price: Number(form.price),
        coverImage: form.coverImage.trim() || null,
        pdfFileUrl: form.pdfFileUrl.trim(),
      });
      toast.success("E-book published");
      setForm(emptyForm);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (book) => {
    if (!window.confirm(`Delete "${book.title}"?`)) return;
    try {
      await api.delete(`/admin/ebooks/${book.id}`);
      setBooks((prev) => prev.filter((b) => b.id !== book.id));
      toast.success("E-book deleted");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (loading) return <div className="py-16 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-brand-red" /></div>;

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-6 items-start" data-testid="admin-ebook-manager">
      <form onSubmit={create} className="rounded-3xl bg-white border border-brand-navy/10 p-6 space-y-4">
        <div className="font-heading font-bold text-lg text-brand-navy flex items-center gap-2">
          <Plus className="w-5 h-5 text-brand-red" /> Add new E-book
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ebook-title">Title</Label>
          <Input id="ebook-title" data-testid="ebook-form-title" value={form.title} onChange={set("title")} placeholder="IELTS Band 8+ Masterclass" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ebook-desc">Description</Label>
          <Textarea id="ebook-desc" data-testid="ebook-form-description" value={form.description} onChange={set("description")} rows={3} placeholder="What's inside this e-book…" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ebook-price">Price (₹)</Label>
          <Input id="ebook-price" data-testid="ebook-form-price" type="number" min="0" value={form.price} onChange={set("price")} placeholder="499" />
        </div>
        <div className="space-y-1.5">
          <Label>Cover Image <span className="text-brand-navy/40 font-normal">(optional)</span></Label>
          <FileUploader
            accept="image/*"
            folder="ebooks/covers"
            value={form.coverImage}
            onChange={(url) => setForm((f) => ({ ...f, coverImage: url }))}
            maxSizeMB={10}
            testIdPrefix="ebook-cover-upload"
          />
        </div>
        <div className="space-y-1.5">
          <Label>PDF File</Label>
          <FileUploader
            accept="application/pdf"
            folder="ebooks/pdf"
            value={form.pdfFileUrl}
            onChange={(url) => setForm((f) => ({ ...f, pdfFileUrl: url }))}
            maxSizeMB={200}
            testIdPrefix="ebook-pdf-upload"
          />
        </div>
        <Button type="submit" disabled={saving} data-testid="ebook-form-submit" className="w-full h-11 rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-bold btn-scale">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
          Publish E-book
        </Button>
      </form>

      <div className="rounded-3xl bg-white border border-brand-navy/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-muted-bg text-brand-navy">
            <tr>
              <th className="text-left px-5 py-3.5 font-semibold">E-book</th>
              <th className="text-left px-5 py-3.5 font-semibold">Price</th>
              <th className="text-left px-5 py-3.5 font-semibold hidden md:table-cell">PDF</th>
              <th className="px-5 py-3.5" />
            </tr>
          </thead>
          <tbody>
            {books.length === 0 && (
              <tr><td colSpan={4} className="text-center py-12 text-brand-navy/50">
                <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-40" /> No e-books yet — publish your first one.
              </td></tr>
            )}
            {books.map((b) => (
              <tr key={b.id} data-testid={`ebook-row-${b.id}`} className="border-t border-brand-navy/5">
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    {b.coverImage && <img src={b.coverImage} alt="" className="w-10 h-14 rounded-lg object-cover ring-1 ring-brand-navy/10 shrink-0" />}
                    <div className="min-w-0">
                      <div className="font-semibold text-brand-navy leading-tight">{b.title}</div>
                      <div className="text-xs text-brand-navy/50 line-clamp-1 max-w-xs">{b.description}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-4 font-bold text-brand-navy">{inr(b.price)}</td>
                <td className="px-5 py-4 hidden md:table-cell">
                  <a href={b.pdfFileUrl} target="_blank" rel="noreferrer" className="text-xs text-brand-red hover:underline break-all">{b.pdfFileUrl.slice(0, 42)}…</a>
                </td>
                <td className="px-5 py-4 text-right">
                  <Button variant="ghost" size="sm" data-testid={`ebook-delete-btn-${b.id}`} onClick={() => remove(b)} className="text-brand-navy/40 hover:text-red-600 hover:bg-red-50 rounded-full">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
